import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import { useForm } from "react-hook-form";
import { errorTypes } from "@/utility/validation";
import { RoutesLink } from "@/utility/RouteLinks";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { holiday, holidayResponse } from "@/interface/holiday.interface";
import { HolidayService } from "@/services/Holiday/HolidayService";
import { showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";

const AddHoliday: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<holiday>({
        mode: "onChange"
    });
    const navigate = useNavigate();
    const { id } = useParams();

    const getHolidayById = async () => {
        if (!id) return;
        try {
            const response = await HolidayService.getHolidayById(id);
            if (response?.status) {
                const data = response.data?.holiday || response.data?.holidays || response.data;
                setValue("name", data?.name || "");
                if (data?.date) {
                    const formattedDate = data.date.split("T")[0];
                    setValue("date", formattedDate);
                }
                setValue("description", data?.description || "");
                setValue("isActive", data?.isActive ?? true);
            }
        } catch (error) {
            ThrowError(error);
        }
    };

    useEffect(() => {
        if (id) {
            getHolidayById();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onSubmit = async (data: holiday) => {
        try {
            setIsLoading(true);
            const payload = {
                name: data?.name,
                date: data?.date,
                description: data?.description,
                isActive: String(data.isActive) === "true",
            };

            let response: holidayResponse;
            if (id) {
                response = await HolidayService.updateHoliday(payload, id);
            } else {
                response = await HolidayService.createHoliday(payload);
            }
            if (response?.status) {
                showToastNotification("success", response?.message);
                navigate(RoutesLink.superAdminHoliday);
            } else {
                showToastNotification("danger", response?.message);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? "Edit Holiday" : "Add Holiday"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />

                <Card>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <FormField
                                {...register("name", { ...errorTypes.required("Name"), ...errorTypes.name })}
                                type="text"
                                name="name"
                                label="Name"
                                minLength={4}
                                maxLength={30}
                                placeholder="Enter Name (e.g. Diwali, Christmas)"
                                error={errors.name?.message}
                                required
                            />

                            <FormField
                                {...register("date", { ...errorTypes.required("Date of Holiday") })}
                                type="date"
                                name="date"
                                label="Date of Holiday"
                                placeholder="Select Date"
                                error={errors.date?.message}
                                required
                            />
                            <FormField
                                {...register("description", { ...errorTypes.required("Description") })}
                                type="textarea"
                                name="description"
                                label="Description"
                                rows={3}
                                placeholder="Provide a brief description or significance of this holiday..."
                                error={errors.description?.message}
                                required
                            />

                            <FormField
                                {...register("isActive", { ...errorTypes.required("Status") })}
                                type="select"
                                value={String(watch("isActive") ?? "")}
                                options={[
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                label="Status"
                                error={errors.isActive?.message}
                                required
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
                            <Button
                                type="button"
                                variant="outline"
                                disabled={isLoading}
                                onClick={() => navigate(RoutesLink.superAdminHoliday)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading} className="min-w-[120px]">
                                {isLoading ? "Saving..." : id ? "Update Holiday" : "Save Holiday"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    );
};

export default AddHoliday;
